import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../store';
import { auth } from '../../firebase/config';
import Icon from '../../components/Icon';
import { FiVideo, FiRadio, FiShield, FiTrendingUp, FiPackage, FiFileText, FiRefreshCw, FiClipboard, FiUsers, FiMapPin } from 'react-icons/fi';
import { fetchChangeOfVendorsData } from '../request_management/change_of_vendor/service/changeOfVendorService';
import { fetchCompanyId } from '../manage_hardware/cctv/service/cctvHardwareService';
import { fetchCompaniesData } from '../companies/service/companiesService';
import { fetchSurveys, fetchComplaints } from '../surveys_management/survey/service/surveyService';
import { subscribeToNewLocationSurveys } from '../surveys_management/new_location_survey/service/newLocationSurveyService';

type DashboardStat = {
    title: string;
    value: number;
    icon: any;
    color: string;
    route: string;
    loading: boolean;
    breakdown?: { label: string; value: number; color: string }[];
};

export default function InchargeDashboard() {
    const pso = useAppSelector((s) => s.user.pso);
    const bank = useAppSelector((s) => s.user.bank);
    const navigate = useNavigate();
    const designation = pso?.designation || '';
    
    // Determine incharge type (remove underscores if present)
    const inchargeType = designation.replace(/_/g, ' ').toLowerCase();
    const isCCTV = inchargeType.includes('cctv');
    const isBAMS = inchargeType.includes('bams');
    const isGuarding = inchargeType.includes('guarding');
    const isMIS = inchargeType.includes('mis');
    
    const [companyId, setCompanyId] = useState<string | null>(null);
    const uid = auth.currentUser?.uid;
    
    const [misStats, setMisStats] = useState<DashboardStat[]>([
        {
            title: 'Branch Surveys',
            value: 0,
            icon: FiClipboard,
            color: 'bg-blue-500',
            route: '/surveys',
            loading: true,
            breakdown: []
        },
        {
            title: 'Manage Vendors',
            value: 0,
            icon: FiUsers,
            color: 'bg-purple-500',
            route: '/companies',
            loading: true,
            breakdown: []
        },
        {
            title: 'Change of Vendor',
            value: 0,
            icon: FiRefreshCw,
            color: 'bg-orange-500',
            route: '/change-of-vendor',
            loading: true,
            breakdown: []
        },
        {
            title: 'New Location Surveys',
            value: 0,
            icon: FiMapPin,
            color: 'bg-green-500',
            route: '/new-location-surveys',
            loading: true,
            breakdown: []
        }
    ]);

    // Fetch companyId
    useEffect(() => {
        if (!uid) return;

        const loadCompanyId = async () => {
            try {
                const id = await fetchCompanyId(uid);
                if (id) {
                    setCompanyId(id);
                }
            } catch (err) {
                console.error('Error fetching company ID:', err);
            }
        };

        loadCompanyId();
    }, [uid]);

    // Fetch MIS dashboard data
    useEffect(() => {
        if (!uid || !isMIS) return;

        const fetchMISData = async () => {
            try {
                // Get companyId first (use state if available, otherwise fetch it)
                let resolvedCompanyId = companyId;
                if (!resolvedCompanyId) {
                    try {
                        resolvedCompanyId = await fetchCompanyId(uid);
                    } catch (err) {
                        console.error('Error fetching company ID:', err);
                    }
                }

                // 1. Fetch Branch Surveys
                let surveysTotal = 0;
                let surveysBreakdown: { label: string; value: number; color: string }[] = [];
                try {
                    // Fetch both surveys and complaints in parallel
                    const [surveys, complaints] = await Promise.all([
                        fetchSurveys(true), // Pass true for isBankUser to get all surveys
                        fetchComplaints(true) // Pass true for isBankUser to get all complaints
                    ]);
                    
                    surveysTotal = surveys.length;
                    
                    // Calculate status for each survey based on complaints
                    let pending = 0;
                    let resolved = 0;
                    
                    // Create a map of complaints by surveyId for O(1) lookup
                    const complaintsBySurveyId = new Map<string, typeof complaints>();
                    complaints.forEach(complaint => {
                        if (complaint.surveyId) {
                            if (!complaintsBySurveyId.has(complaint.surveyId)) {
                                complaintsBySurveyId.set(complaint.surveyId, []);
                            }
                            complaintsBySurveyId.get(complaint.surveyId)!.push(complaint);
                        }
                    });
                    
                    surveys.forEach(survey => {
                        const surveyComplaints = complaintsBySurveyId.get(survey.id) || [];
                        
                        if (surveyComplaints.length === 0) {
                            // No complaints = Resolved
                            resolved++;
                        } else {
                            // Check if any complaints are pending
                            const hasPending = surveyComplaints.some(c => {
                                const status = (c.status || '').toLowerCase().trim();
                                return status === 'pending' || status === '' || !c.status;
                            });
                            if (hasPending) {
                                pending++;
                            } else {
                                // All complaints are resolved
                                resolved++;
                            }
                        }
                    });
                    
                    surveysBreakdown = [
                        { label: 'Resolved', value: resolved, color: 'bg-green-500' },
                        { label: 'Pending', value: pending, color: 'bg-yellow-500' }
                    ];
                } catch (error) {
                    console.error('Error fetching branch surveys:', error);
                }

                // 2. Fetch Manage Vendors (Companies)
                let vendorsTotal = 0;
                let vendorsBreakdown: { label: string; value: number; color: string }[] = [];
                try {
                    const companies = await fetchCompaniesData(uid || '', false);
                    vendorsTotal = companies.length;
                    // Count in single pass
                    let cctv = 0, bams = 0, guard = 0, other = 0;
                    companies.forEach(c => {
                        const type = c.companyType || '';
                        if (type === 'CCTV') cctv++;
                        else if (type === 'BAMS') bams++;
                        else if (type === 'Guard Company') guard++;
                        else other++;
                    });
                    vendorsBreakdown = [
                        { label: 'Guard Company', value: guard, color: 'bg-blue-500' },
                        { label: 'CCTV', value: cctv, color: 'bg-purple-500' },
                        { label: 'BAMS', value: bams, color: 'bg-green-500' },
                        { label: 'Other', value: other, color: 'bg-gray-500' }
                    ];
                } catch (error) {
                    console.error('Error fetching vendors:', error);
                }

                // 3. Fetch Change of Vendor
                let changeOfVendorTotal = 0;
                let changeOfVendorBreakdown: { label: string; value: number; color: string }[] = [];
                try {
                    const changeOfVendors = await fetchChangeOfVendorsData(uid || '', false, false, designation);
                    changeOfVendorTotal = changeOfVendors.length;
                    // Count in single pass
                    let pending = 0, approved = 0, rejected = 0, cancelled = 0;
                    changeOfVendors.forEach(c => {
                        const status = c.status || '';
                        if (status === 'Pending') pending++;
                        else if (status === 'Approved') approved++;
                        else if (status === 'Rejected') rejected++;
                        else if (status === 'Cancelled') cancelled++;
                    });
                    changeOfVendorBreakdown = [
                        { label: 'Pending', value: pending, color: 'bg-yellow-500' },
                        { label: 'Approved', value: approved, color: 'bg-green-500' },
                        { label: 'Rejected', value: rejected, color: 'bg-red-500' },
                        { label: 'Cancelled', value: cancelled, color: 'bg-gray-500' }
                    ];
                } catch (error) {
                    console.error('Error fetching change of vendor:', error);
                }

                // 4. Fetch New Location Surveys
                let newLocationSurveysTotal = 0;
                let newLocationSurveysBreakdown: { label: string; value: number; color: string }[] = [];
                try {
                    // Use subscription to get real-time data with shorter timeout
                    const surveysPromise = new Promise<any[]>((resolve) => {
                        let resolved = false;
                        const unsubscribe = subscribeToNewLocationSurveys((surveys) => {
                            if (!resolved) {
                                resolved = true;
                                unsubscribe();
                                resolve(surveys);
                            }
                        });
                        // Timeout after 2 seconds (reduced from 5)
                        setTimeout(() => {
                            if (!resolved) {
                                resolved = true;
                                unsubscribe();
                                resolve([]);
                            }
                        }, 2000);
                    });
                    const newLocationSurveys = await surveysPromise;
                    newLocationSurveysTotal = newLocationSurveys.length;
                    // Count in single pass
                    let pending = 0, submitted = 0, approved = 0, rejected = 0, completed = 0, cancelled = 0;
                    newLocationSurveys.forEach(s => {
                        const status = (s.status || '').toLowerCase();
                        if (status === 'completed') completed++;
                        else if (status === 'approved') approved++;
                        else if (status === 'submitted') submitted++;
                        else if (status === 'pending') pending++;
                        else if (status === 'rejected') rejected++;
                        else if (status === 'cancelled') cancelled++;
                    });
                    newLocationSurveysBreakdown = [
                        { label: 'Completed', value: completed, color: 'bg-green-500' },
                        { label: 'Approved', value: approved, color: 'bg-blue-500' },
                        { label: 'Submitted', value: submitted, color: 'bg-yellow-500' },
                        { label: 'Pending', value: pending, color: 'bg-orange-500' },
                        { label: 'Rejected', value: rejected, color: 'bg-red-500' },
                        { label: 'Cancelled', value: cancelled, color: 'bg-gray-500' }
                    ];
                } catch (error) {
                    console.error('Error fetching new location surveys:', error);
                }

                // Update stats
                setMisStats([
                    {
                        title: 'Branch Surveys',
                        value: surveysTotal,
                        icon: FiClipboard,
                        color: 'bg-blue-500',
                        route: '/surveys',
                        loading: false,
                        breakdown: surveysBreakdown
                    },
                    {
                        title: 'Manage Vendors',
                        value: vendorsTotal,
                        icon: FiUsers,
                        color: 'bg-purple-500',
                        route: '/companies',
                        loading: false,
                        breakdown: vendorsBreakdown
                    },
                    {
                        title: 'Change of Vendor',
                        value: changeOfVendorTotal,
                        icon: FiRefreshCw,
                        color: 'bg-orange-500',
                        route: '/change-of-vendor',
                        loading: false,
                        breakdown: changeOfVendorBreakdown
                    },
                    {
                        title: 'New Location Surveys',
                        value: newLocationSurveysTotal,
                        icon: FiMapPin,
                        color: 'bg-green-500',
                        route: '/new-location-surveys',
                        loading: false,
                        breakdown: newLocationSurveysBreakdown
                    }
                ]);
            } catch (error) {
                console.error('Error fetching MIS data:', error);
            }
        };

        fetchMISData();
    }, [uid, isMIS, designation, companyId]);

    const handleStatClick = useCallback((route: string) => {
        navigate(route);
    }, [navigate]);

    // For MIS users, show detailed dashboard with charts
    if (isMIS) {
        return (
            <div className="space-y-6 p-6">
                {/* Header */}
                <div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                        Incharge MIS Dashboard
                    </h2>
                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                        Welcome back, {pso?.name || 'User'}. Overview of all operations.
                    </p>
                </div>

                {/* Stats Grid with Charts */}
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                    {misStats.map((stat, index) => (
                        <div
                            key={index}
                            onClick={() => handleStatClick(stat.route)}
                            className="group cursor-pointer rounded-xl border-2 border-gray-200 bg-white p-6 shadow-sm transition-all hover:border-gray-300 hover:shadow-md dark:border-gray-700 dark:bg-gray-800 dark:hover:border-gray-600"
                        >
                            <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center gap-3">
                                    <div className={`rounded-lg ${stat.color} p-3`}>
                                        <Icon icon={stat.icon} className="h-6 w-6 text-white" />
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                                            {stat.title}
                                        </p>
                                        <p className="mt-1 text-3xl font-bold text-gray-900 dark:text-gray-100">
                                            {stat.loading ? (
                                                <span className="inline-block h-8 w-8 animate-spin rounded-full border-2 border-solid border-gray-400 border-r-transparent"></span>
                                            ) : (
                                                stat.value
                                            )}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Chart/Breakdown */}
                            {stat.breakdown && stat.breakdown.length > 0 && (
                                <div className="mt-4 space-y-3">
                                    {stat.breakdown.map((item, idx) => {
                                        const percentage = stat.value > 0 ? (item.value / stat.value) * 100 : 0;
                                        return (
                                            <div key={idx} className="space-y-1">
                                                <div className="flex items-center justify-between text-sm">
                                                    <span className="font-medium text-gray-700 dark:text-gray-300">
                                                        {item.label}
                                                    </span>
                                                    <span className="text-gray-600 dark:text-gray-400">
                                                        {item.value} ({percentage.toFixed(1)}%)
                                                    </span>
                                                </div>
                                                <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                                                    <div
                                                        className={`h-full ${item.color} transition-all`}
                                                        style={{ width: `${percentage}%` }}
                                                    />
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {/* Total bar chart */}
                            {stat.breakdown && stat.breakdown.length > 0 && stat.value > 0 && (
                                <div className="mt-4 flex h-8 gap-1">
                                    {stat.breakdown.map((item, idx) => {
                                        const percentage = (item.value / stat.value) * 100;
                                        return (
                                            <div
                                                key={idx}
                                                className={`${item.color} transition-all group-hover:opacity-90`}
                                                style={{ width: `${percentage}%` }}
                                                title={`${item.label}: ${item.value}`}
                                            />
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    // For other incharge types, show the original dashboard
    const stats = [
        {
            title: 'Total Hardware',
            value: '0',
            icon: isCCTV ? FiVideo : isBAMS ? FiRadio : FiPackage,
            color: 'bg-blue-500',
            description: isCCTV ? 'CCTV Systems' : isBAMS ? 'BAMS Systems' : 'Items'
        },
        {
            title: 'Pending Requests',
            value: '0',
            icon: FiFileText,
            color: 'bg-yellow-500',
            description: 'Awaiting approval'
        },
        {
            title: 'Active Installations',
            value: '0',
            icon: FiTrendingUp,
            color: 'bg-green-500',
            description: 'In progress'
        },
        {
            title: 'Completed',
            value: '0',
            icon: isGuarding ? FiShield : FiTrendingUp,
            color: 'bg-purple-500',
            description: 'This month'
        }
    ];

    return (
        <div className="space-y-6 p-6">
            {/* Header */}
            <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                    {designation || 'Incharge'} Dashboard
                </h2>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                    Welcome back, {pso?.name || 'User'}. Manage your operations from here.
                </p>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
                {stats.map((stat, index) => (
                    <div
                        key={index}
                        className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-800"
                    >
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                                    {stat.title}
                                </p>
                                <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-gray-100">
                                    {stat.value}
                                </p>
                                <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">
                                    {stat.description}
                                </p>
                            </div>
                            <div className={`rounded-full ${stat.color} p-3`}>
                                <Icon icon={stat.icon} className="h-6 w-6 text-white" />
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Quick Actions */}
            <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-800">
                <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">
                    Quick Actions
                </h3>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {isCCTV && (
                        <button className="flex items-center gap-3 rounded-lg border border-gray-200 p-4 text-left transition hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-700">
                            <Icon icon={FiVideo} className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                            <div>
                                <div className="font-medium text-gray-900 dark:text-gray-100">CCTV Hardware</div>
                                <div className="text-sm text-gray-500 dark:text-gray-400">Manage CCTV systems</div>
                            </div>
                        </button>
                    )}
                    {isBAMS && (
                        <button className="flex items-center gap-3 rounded-lg border border-gray-200 p-4 text-left transition hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-700">
                            <Icon icon={FiRadio} className="h-5 w-5 text-green-600 dark:text-green-400" />
                            <div>
                                <div className="font-medium text-gray-900 dark:text-gray-100">BAMS Hardware</div>
                                <div className="text-sm text-gray-500 dark:text-gray-400">Manage BAMS systems</div>
                            </div>
                        </button>
                    )}
                    {isGuarding && (
                        <button className="flex items-center gap-3 rounded-lg border border-gray-200 p-4 text-left transition hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-700">
                            <Icon icon={FiShield} className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                            <div>
                                <div className="font-medium text-gray-900 dark:text-gray-100">Guarding Operations</div>
                                <div className="text-sm text-gray-500 dark:text-gray-400">Manage guarding services</div>
                            </div>
                        </button>
                    )}
                </div>
            </div>

            {/* Recent Activity */}
            <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-800">
                <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">
                    Recent Activity
                </h3>
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    <p>No recent activity to display</p>
                </div>
            </div>
        </div>
    );
}
